import { useEffect, useState } from 'react';
import { createClient } from 'contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';

export function RentalConditions() {
  const [sections, setSections] = useState([]);
  const [openSection, setOpenSection] = useState(null);

  // Render options using your site's hardcoded styles
  const renderOptions = {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1 className="font-bold text-4xl text-gray-900 mb-6">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className="font-bold text-3xl text-gray-900 mb-6">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className="font-semibold text-xl text-gray-900 mb-3 group-hover:text-brand transition-colors">
          {children}
        </h3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h4 className="font-semibold text-lg text-gray-900 mb-3">{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <h5 className="font-semibold text-base text-gray-900 mb-3">{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <h6 className="font-semibold text-sm text-gray-900 mb-3">{children}</h6>
      ),
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p className="text-gray-600 leading-relaxed">{children}</p>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="list-disc pl-6 space-y-2 text-gray-600">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="list-decimal pl-6 space-y-2 text-gray-600">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className="text-gray-600 leading-relaxed">{children}</li>
      ),
    },
  };

  useEffect(() => {
    const client = createClient({
      space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
      accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
    });

    client
      .getEntries({
        content_type: 'rentalCondition',
        order: 'fields.position'
      })
      .then((response) => {
        const fetchedSections = response.items.map((item) => ({
          id: item.sys.id,
          title: item.fields.title,
          content: item.fields.content,
        }));
        setSections(fetchedSections);
        if (fetchedSections.length > 0) {
          setOpenSection(fetchedSections[0].id);
        }
      })
      .catch(console.error);
  }, []);

  const toggleSection = (sectionId) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-white">
      <h1 className="text-5xl font-bold text-gray-900 mb-16 text-center">
        Condiciones de alquiler
      </h1>
      <div className="space-y-6">
        {sections.map((section, index) => (
          <section
            key={section.id}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleSection(section.id);
              }}
              className="w-full p-8 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                <span className="text-brand mr-3">{index + 1}.</span>
                {section.title}
              </h2>
              <svg
                className={`w-6 h-6 text-gray-400 transform transition-transform ${
                  openSection === section.id ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`transition-all duration-300 ease-in-out ${
                openSection === section.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}
            >
              <div className="p-8 pt-0 space-y-8">
                {typeof section.content === 'string'
                  ? section.content.split('\n').map((line, i) => (
                      <p key={i} className="text-gray-600 leading-relaxed">{line}</p>
                    ))
                  : documentToReactComponents(section.content, renderOptions)}
              </div>
            </div>
          </section>
        ))}
      </div>
      <div className="mt-12 text-center">
        <a
          href="/consulta-disponibilidad"
          className="inline-block bg-brand text-white py-3 px-6 rounded-lg hover:bg-brand-dark transition-colors"
        >
          Consulta disponibilidad
        </a>
      </div>
    </div>
  );
}
